import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box } from "@mui/material";
import { useGetPhotosQuery } from "@store/photoApi";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Photo } from "@models/photo";
import "swiper/css";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import Avatar from "@components/Avatar/Avatar";
import { useGetAlbumPhotosQuery } from "@store/albumApi";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import "./image-gallery.css";
import StateIcon from "@components/StateIcon/StateIcon";
import Subtitles from "@components/Subtitles";
import { ScreenSize } from "@constants/screen-size";

/** Delay before companion reacts to a new slide so rapid swipes coalesce to one `NewPhoto`. */
const COMPANION_PHOTO_DEBOUNCE_MS = 2_000;

const AvatarContainer = styled.div`
  position: absolute;
  bottom: 96px;
  right: 4px;
  width: 20%;
  height: 25%;
  min-width: 120px;
  min-height: 90px;
  max-width: 300px;
  max-height: 300px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
`;

const State = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: calc(100dvh - 206px);

  @media screen and (orientation: landscape) {
    top: calc(100dvh - 132px);
  }
`;

const SubtitlesContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-60%);
  display: flex;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  bottom: 160px;
  width: 70%;
  max-height: 50dvh;

  @media (max-width: ${ScreenSize.md}px) {
    transform: translateX(-70%);
  }

  @media (max-width: ${ScreenSize.sm}px) {
    transform: translateX(-75%);
    width: 65%;
  }

  @media (orientation: landscape) {
    bottom: 145px;
  }
`;

const PhotosCompanion: FC<{
  albumId?: string;
  sessionId: string;
  companionName: string;
}> = ({ albumId, sessionId, companionName }) => {
  const { t } = useTranslation();
  const { ready } = useSelector((state: RootState) => state.avatar);
  const [companionPhotoId, setCompanionPhotoId] = useState<string | undefined>();
  /** Visible slide; updated immediately so voice `UserInput` matches the image on screen. */
  const [slidePhotoId, setSlidePhotoId] = useState<string | undefined>();
  const [gallerySlideEpoch, setGallerySlideEpoch] = useState(0);
  const companionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { text, subtitlesOn } = useSelector((state: RootState) => state.avatar);
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: allPhotos, isLoading: isAllPhotosLoading } = useGetPhotosQuery(
    { patientId: selectedPatientId! },
    {
      skip: !selectedPatientId || !!albumId,
    },
  );
  const { data: albumPhotos, isLoading: isAlbumPhotosLoading } =
    useGetAlbumPhotosQuery(
      { albumId: albumId! },
      {
        skip: !albumId,
      },
    );

  const photos = useMemo(() => {
    return (albumId ? albumPhotos : allPhotos)?.data || [];
  }, [albumId, albumPhotos, allPhotos]);

  const galleryItems: ReactImageGalleryItem[] = useMemo(() => {
    return photos.map((photo) => ({
      original: photo.fileUrl,
      thumbnail: photo.fileUrl,
    }));
  }, [photos]);

  const isLoading = useMemo(() => {
    return albumId ? isAlbumPhotosLoading : isAllPhotosLoading;
  }, [albumId, isAlbumPhotosLoading, isAllPhotosLoading]);

  useEffect(() => {
    if (companionDebounceRef.current) {
      clearTimeout(companionDebounceRef.current);
      companionDebounceRef.current = null;
    }
    if (photos?.length) {
      setCompanionPhotoId(photos[0].id);
      setSlidePhotoId(photos[0].id);
    } else {
      setCompanionPhotoId(undefined);
      setSlidePhotoId(undefined);
    }
  }, [photos]);

  useEffect(() => {
    return () => {
      if (companionDebounceRef.current) {
        clearTimeout(companionDebounceRef.current);
      }
    };
  }, []);

  const scheduleCompanionPhotoId = useCallback((photoId: string | undefined) => {
    if (companionDebounceRef.current) {
      clearTimeout(companionDebounceRef.current);
      companionDebounceRef.current = null;
    }
    if (!photoId) {
      setCompanionPhotoId(undefined);
      setSlidePhotoId(undefined);
      return;
    }
    companionDebounceRef.current = setTimeout(() => {
      companionDebounceRef.current = null;
      setCompanionPhotoId(photoId);
    }, COMPANION_PHOTO_DEBOUNCE_MS);
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <Box position="relative">
      {ready && (
        <>
          {galleryItems.length ? (
            <ImageGallery
              items={galleryItems}
              onSlide={(index) => {
                const id = photos[index]?.id;
                setGallerySlideEpoch((n) => n + 1);
                setSlidePhotoId(id);
                scheduleCompanionPhotoId(id);
              }}
              showPlayButton={false}
              showFullscreenButton={false}
              additionalClass="image-gallery"
            />
          ) : (
            <Box display="flex" justifyContent="center">
              <Alert severity="warning">{t("photosCompanion.noPhotos")}</Alert>
            </Box>
          )}
        </>
      )}

      {subtitlesOn && text && (
        <SubtitlesContainer>
          <Subtitles text={text} />
        </SubtitlesContainer>
      )}
      <State>
        <StateIcon />
      </State>

      <AvatarContainer>
        <Avatar
          sessionId={sessionId}
          photoId={companionPhotoId}
          voiceContextPhotoId={slidePhotoId}
          gallerySlideEpoch={gallerySlideEpoch}
          companionName={companionName}
        />
      </AvatarContainer>
    </Box>
  );
};
export default PhotosCompanion;
